import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export interface PillTabItem<T extends string = string> {
  id: T;
  label: string;
  icon: LucideIcon;
  /** When true the tab is non-interactive and dimmed. */
  disabled?: boolean;
  /** Icon shown in place of `icon` while disabled (e.g. a lock). */
  lockedIcon?: LucideIcon;
}

interface PillTabsProps<T extends string> {
  tabs: ReadonlyArray<PillTabItem<T>>;
  value: T;
  onChange: (id: T) => void;
  /** Extra classes for the `Tabs` root (e.g. responsive visibility). */
  className?: string;
}

/**
 * PillTabs — the canonical rounded-pill tab strip used across v2 surfaces.
 *
 * Extracted from the project-dashboard tab strip so the same component (and
 * therefore the same look) is shared by project pages and the admin eval
 * sub-nav. Presentational only: it renders the shadcn `Tabs` primitives and
 * reports selections via `onChange` — callers own routing/state.
 */
export function PillTabs<T extends string>({
  tabs,
  value,
  onChange,
  className,
}: PillTabsProps<T>) {
  return (
    <Tabs
      value={value}
      onValueChange={(v) => onChange(v as T)}
      className={cn("max-w-full overflow-x-auto scrollbar-hide", className)}
    >
      <TabsList className="inline-flex h-9 items-center gap-0.5 rounded-full border border-border bg-secondary/70 p-1 backdrop-blur-sm dark:bg-secondary/40">
        {tabs.map((tab) => {
          const Icon = tab.disabled && tab.lockedIcon ? tab.lockedIcon : tab.icon;
          const isActive = tab.id === value;
          return (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              disabled={tab.disabled}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-all",
                "text-muted-foreground hover:text-foreground",
                "data-[state=active]:shadow-sm",
              )}
              style={
                isActive
                  ? {
                      background: "var(--gradient-primary)",
                      color: "var(--primary-foreground)",
                    }
                  : undefined
              }
            >
              <Icon className="size-3.5" />
              {tab.label}
            </TabsTrigger>
          );
        })}
      </TabsList>
    </Tabs>
  );
}
